import Vue from 'vue'
import { readLanguage } from './locale'
import { createHumanVerification, verificationError } from './human-verification/core'
import { verificationMessages } from './human-verification/messages'
import './human-verification/widget.css'

export const humanVerificationState = Vue.observable({
  verified: false,
  notice: null
})

export const humanVerificationResumedEvent = 'uplus:human-verification-resumed'

let client = null
let clientBase = ''
let pausedError = null
let generation = 0
let requiresVerification = true
let expiryTimer = null

function normalizedBase(apiBase) {
  return String(apiBase || clientBase || '').replace(/\/+$/, '')
}

function absoluteRequestUrl(url, apiBase) {
  const value = String(url || '')
  if (/^https?:\/\//i.test(value)) return value
  return normalizedBase(apiBase) + '/' + value.replace(/^\/+/, '')
}

function stopExpiryTimer() {
  if (expiryTimer !== null) window.clearTimeout(expiryTimer)
  expiryTimer = null
}

function setNotice(value) {
  humanVerificationState.notice = value
}

function describeError(error) {
  const messages = verificationMessages(readLanguage())
  return messages[error && error.code] || messages.unavailable
}

function rawRequest(options) {
  const controller = new AbortController()
  const signal = options.signal
  const abort = () => controller.abort()
  if (signal && signal.aborted) abort()
  else if (signal) signal.addEventListener('abort', abort)

  const timer = window.setTimeout(abort, options.timeout || 15000)
  return window.fetch(options.url, {
    method: options.method,
    headers: options.headers || options.header || {},
    signal: controller.signal,
    cache: 'no-store',
    redirect: 'error',
    credentials: 'omit'
  }).then(async response => ({
    statusCode: response.status,
    data: await response.json()
  })).catch(() => {
    throw verificationError(signal && signal.aborted ? 'cancelled' : 'unavailable')
  }).finally(() => {
    window.clearTimeout(timer)
    if (signal) signal.removeEventListener('abort', abort)
  })
}

function getClient(apiBase) {
  const base = normalizedBase(apiBase)
  if (!base) throw verificationError('unavailable')
  if (!client || clientBase !== base) {
    if (client) client.clear()
    stopExpiryTimer()
    clientBase = base
    client = createHumanVerification({
      apiBase: base,
      request: rawRequest,
      async challenge(config, signal) {
        stopExpiryTimer()
        requiresVerification = true
        humanVerificationState.verified = false
        const widget = await import('./human-verification/widget')
        return widget.showTurnstile(config, {
          signal,
          locale: readLanguage(),
          fullPage: true
        })
      }
    })
  }
  return client
}

function grantAccess(sent, verification) {
  stopExpiryTimer()
  humanVerificationState.verified = true
  requiresVerification = Boolean(sent.token)
  pausedError = null
  setNotice(null)

  if (!requiresVerification) return

  const schedule = () => {
    const remaining = verification.getExpiresAt() - Date.now()
    if (remaining <= 0) {
      pause(verificationError('invalid'))
      return
    }
    expiryTimer = window.setTimeout(schedule, Math.min(remaining, 2147483647))
  }
  schedule()
}

function pause(error) {
  const failure = error && error.humanVerification
    ? error
    : verificationError('unavailable')
  stopExpiryTimer()
  humanVerificationState.verified = false
  failure.message = describeError(failure)
  if (pausedError !== failure || (humanVerificationState.notice && humanVerificationState.notice.retrying)) {
    pausedError = failure
    setNotice({ code: failure.code, retrying: false })
  }
  return failure
}

export function checkVerificationAccess(apiBase = clientBase) {
  if (!client || !apiBase) return
  const verification = getClient(apiBase)
  if (
    humanVerificationState.verified &&
    requiresVerification &&
    verification.getExpiresAt() <= Date.now()
  ) {
    pause(verificationError('invalid'))
  }
}

export async function ensureHumanVerification(signal, background = false, apiBase = clientBase) {
  const verification = getClient(apiBase)
  if (signal && signal.aborted) throw verificationError('cancelled')
  checkVerificationAccess(apiBase)
  if (background && pausedError) throw pausedError

  const current = generation
  try {
    const sent = await verification.ensure(signal)
    if (current !== generation || !verification.isCurrent(sent)) throw verificationError('cancelled')
    if (sent.token && verification.getExpiresAt() <= Date.now()) throw verificationError('unavailable')
    grantAccess(sent, verification)
    return sent
  } catch (error) {
    if ((signal && signal.aborted) || current !== generation) throw verificationError('cancelled')
    throw pause(error)
  }
}

function isBackgroundRequest(config) {
  const params = (config && config.params) || {}
  return config && (
    config.verification === 'background' ||
    Number(params.requestTimes) === 10 ||
    Number(params.hideLoad) === 10 ||
    params.hideLoad === true
  )
}

function sanitizedHeaders(headers, token) {
  const result = {}
  Object.keys(headers || {}).forEach(key => {
    const name = String(key).toLowerCase()
    if (name !== 'x-human-verification' && name !== 'x-turnstile-token') {
      result[key] = headers[key]
    }
  })
  if (token) result['X-Human-Verification'] = token
  return result
}

export async function authorizeHumanRequest(config, apiBase) {
  const verification = getClient(apiBase)
  const url = absoluteRequestUrl(config.url, config.baseURL || apiBase)
  const background = isBackgroundRequest(config)
  delete config.verification
  if (!verification.isProtected(url, config.method)) return config

  const sent = await ensureHumanVerification(config.signal, background, apiBase)
  if (!verification.isCurrent(sent)) throw verificationError('cancelled')
  config.headers = sanitizedHeaders(config.headers, sent.token)
  config.__humanVerificationSent = sent
  return config
}

function inspectSentResponse(status, data, sent, apiBase) {
  const verification = getClient(apiBase)
  const wasCurrent = verification.isCurrent(sent)
  const failure = verification.inspectResponse(status, data, sent)
  if (failure && wasCurrent) pause(failure)
  return failure
}

export function inspectHumanVerificationResponse(config, status, data, apiBase) {
  const verification = getClient(apiBase)
  const url = absoluteRequestUrl(config && config.url, (config && config.baseURL) || apiBase)
  if (!config || !verification.isProtected(url, config.method) || !config.__humanVerificationSent) return null
  return inspectSentResponse(status, data, config.__humanVerificationSent, apiBase)
}

export function humanVerificationResult(error) {
  if (!error || !error.humanVerification) return null
  error.message = error.message && error.message !== error.code
    ? error.message
    : describeError(error)
  return {
    success: false,
    statusCode: error.code === 'unavailable'
      ? '50310'
      : error.code === 'cancelled'
        ? 'HV_CANCELLED'
        : '40311',
    message: error.message
  }
}

export async function humanFetch(url, options = {}, apiBase) {
  const verification = getClient(apiBase)
  const protectedRequest = verification.isProtected(url, options.method)
  const {
    timeoutMs = 0,
    verification: verificationMode,
    signal: outerSignal,
    ...fetchOptions
  } = options

  if (!protectedRequest) {
    return window.fetch(url, { ...fetchOptions, signal: outerSignal })
  }

  const controller = new AbortController()
  const abort = () => controller.abort()
  let timeout = null
  if (outerSignal && outerSignal.aborted) controller.abort()
  else if (outerSignal) outerSignal.addEventListener('abort', abort, { once: true })

  try {
    const sent = await ensureHumanVerification(
      controller.signal,
      verificationMode === 'background',
      apiBase
    )
    if (controller.signal.aborted || !verification.isCurrent(sent)) throw verificationError('cancelled')

    const headers = new Headers(fetchOptions.headers || {})
    headers.delete('X-Turnstile-Token')
    headers.delete('X-Human-Verification')
    if (sent.token) headers.set('X-Human-Verification', sent.token)

    if (timeoutMs > 0) timeout = window.setTimeout(abort, timeoutMs)
    const response = await window.fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
      cache: 'no-store',
      redirect: 'error'
    })

    if (response.status === 403 || response.status === 503) {
      let body = {}
      try { body = await response.clone().json() } catch (_) {}
      const failure = inspectSentResponse(response.status, body, sent, apiBase)
      if (failure) throw failure
    }
    return response
  } catch (error) {
    if (error && error.humanVerification) {
      error.message = describeError(error)
    }
    throw error
  } finally {
    if (timeout !== null) window.clearTimeout(timeout)
    if (outerSignal) outerSignal.removeEventListener('abort', abort)
  }
}

export function clearHumanVerification() {
  generation += 1
  stopExpiryTimer()
  humanVerificationState.verified = false
  requiresVerification = true
  if (client) client.clear()
  pausedError = null
  setNotice(null)
}

export async function resumeHumanVerification(apiBase = clientBase) {
  if (humanVerificationState.notice && humanVerificationState.notice.retrying) return
  setNotice({
    code: (humanVerificationState.notice && humanVerificationState.notice.code) || 'unavailable',
    retrying: true
  })
  try {
    await ensureHumanVerification(null, false, apiBase)
    window.dispatchEvent(new Event(humanVerificationResumedEvent))
  } catch (_) {
    // pause() already exposes the recovery state in the full-page gate.
  }
}

export function clearLoginSession() {
  clearHumanVerification()
  window.localStorage.removeItem('token')
  window.localStorage.removeItem('userInfo')
  window.sessionStorage.removeItem('uplusStrategyDetail')
}
